// BulkDeleteConfirmationModal.js
import React, { useMemo } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Text,
    VStack,
    Box,
    List,
    ListItem,
    Badge,
    Alert,
    AlertIcon,
    AlertDescription,
} from '@chakra-ui/react';

const BulkDeleteConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
    selectedConversations,
    conversations,
    currentConversationId,
    isDeleting
}) => {
    const selectedConversationDetails = useMemo(() => {
        return conversations.filter(conv => selectedConversations.has(conv.id));
    }, [conversations, selectedConversations]);

    const isCurrentConversationSelected = selectedConversations.has(currentConversationId);
    const selectedCount = selectedConversations.size;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    Delete {selectedCount} Conversation{selectedCount === 1 ? '' : 's'}
                </ModalHeader>
                
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        <Text>
                            Are you sure you want to delete the following conversations? 
                            This action cannot be undone.
                        </Text>

                        {/* Warning for current conversation */}
                        {isCurrentConversationSelected && (
                            <Alert status="warning" borderRadius="md">
                                <AlertIcon />
                                <AlertDescription>
                                    Your current conversation is included in this selection. 
                                    You'll be moved to a new conversation after deletion.
                                </AlertDescription>
                            </Alert>
                        )}

                        {/* List of conversations to delete */}
                        <Box maxH="200px" overflowY="auto" border="1px" borderColor="gray.200" borderRadius="md" p={3}>
                            <List spacing={2}>
                                {selectedConversationDetails.map((conv) => (
                                    <ListItem key={conv.id}>
                                        <Text fontSize="sm">
                                            {conv.name}
                                            {conv.id === currentConversationId && (
                                                <Badge ml={2} colorScheme="orange" size="sm">
                                                    Current
                                                </Badge>
                                            )}
                                        </Text>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>

                        <Text fontSize="sm" color="gray.600">
                            {selectedCount} conversation{selectedCount === 1 ? '' : 's'} will be permanently deleted.
                        </Text>
                    </VStack>
                </ModalBody>

                <ModalFooter>
                    <Button 
                        variant="ghost" 
                        mr={3} 
                        onClick={onClose}
                        isDisabled={isDeleting}
                    >
                        Cancel
                    </Button>
                    <Button 
                        colorScheme="red" 
                        onClick={onConfirm}
                        isLoading={isDeleting}
                        loadingText="Deleting..."
                    >
                        Delete {selectedCount} Conversation{selectedCount === 1 ? '' : 's'}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default BulkDeleteConfirmationModal;